import { heart, heartOutline, arrowUpOutline, arrowDownOutline, checkmarkOutline, lockClosedOutline, radioOutline } from "ionicons/icons"
import moment from "moment"
import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import { IonIcon, IonLoading, IonRow } from "@ionic/react"

import { toggleWatchlistItem } from "../../redux/slices/auctionItemsSlice"
import { WATCH_LIST, getEndPoint } from "../../util/api/enpoints"
import Text from "../CommonStyles/Text"
import { AuctionItem, UserBidStatus } from "./Index"
import { ErrorMessage } from "./styledComponents"
import { saveScrollAndNavigate } from "./helpers"
import { DateTime } from "luxon"

interface Params { eventcode: string }

export const CardViewItems = ({ state, items }) => {
  const local = DateTime.local()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { eventcode } = useParams()
  const accountStatus = useSelector((state: any) => state.user.accountstatus)

  const onItemClick = (itemId: number) => {
    saveScrollAndNavigate(itemId, eventcode, navigate)
  }

  const getLocalDateString = (dateString) => {
    const centralDate = DateTime.fromISO(dateString, { zone: 'America/Chicago' })
    const localDate = centralDate.setZone(local.zoneName)
    return localDate.toString()
  }

  const closingTime = (closing: string) => {
    const then: any = moment(closing)
    const now: any = moment()
    const diff = moment.duration(then.diff(now))
    const days = Math.floor(diff.asDays())
    const hours = diff.hours()
    const min = diff.minutes()
    if (days >= 1) return `${days}d ${hours}h`
    if (hours >= 1) return `${hours}h ${min}m`
    const sec = diff.seconds()
    const pad = (n: number) => (n < 10 ? '0' + n : String(n))
    return `${min}:${pad(sec)}`
  }

  const itemStatus = (item: any) => {
    if (item.bidstatus === "biddingclosed" || (item.quantityavailable === 0 && item.unlimitedquantity === false)) {
      return "Ended"
    }
    return closingTime(getLocalDateString(item.closingcst))
  }

  const getStatus = (item: AuctionItem): UserBidStatus => {
    const stillOpen = moment().diff(moment(getLocalDateString(item.closingcst))) < 0
    if (item.currentlywinningbid && item.accountbiddingon && stillOpen) return "highBidder"
    if (item.currentlywinningbid && item.accountbiddingon && !stillOpen) return "winner"
    if (!item.currentlywinningbid && item.accountbiddingon) return "losingBidder"
    return "default"
  }

  const onWatchListClick = (itemid: number) => (e: any) => {
    e.stopPropagation()
    dispatch(toggleWatchlistItem({ url: getEndPoint(WATCH_LIST), itemid }))
  }

  if (state === "loading") {
    return <IonRow><IonLoading isOpen></IonLoading></IonRow>
  }

  if (state === "failed") {
    return <ErrorMessage>Sorry, we are unable to fetch auction items at the moment.</ErrorMessage>
  }

  return (
    <>
      {items?.map((item: AuctionItem) => {
        const status = getStatus(item)
        const isClosed = item.bidstatus === "biddingclosed"
        const isSoldOut = item.quantityavailable === 0 && item.unlimitedquantity === false
        const isWon = (isClosed || isSoldOut) && item.accountbiddingon && item.currentlywinningbid
        const isClosedOther = (isClosed || isSoldOut) && !isWon
        const isLive = item.itemisliveonly && !isClosed

        let cardCls = 'as-card'
        if (status === 'highBidder') cardCls += ' as-card--win'
        else if (status === 'losingBidder') cardCls += ' as-card--out'
        else if (isWon || isClosedOther) cardCls += ' as-card--closed'

        // Pill
        let pill: React.ReactNode = null
        if (status === 'highBidder') {
          pill = (
            <span className="as-pill as-pill--win">
              <IonIcon icon={arrowUpOutline} style={{ width: 12, height: 12 }} />
              Winning
            </span>
          )
        } else if (status === 'losingBidder') {
          pill = (
            <span className="as-pill as-pill--out">
              <IonIcon icon={arrowDownOutline} style={{ width: 12, height: 12 }} />
              Outbid
            </span>
          )
        } else if (isLive) {
          pill = (
            <span className="as-pill as-pill--live">
              <IonIcon icon={radioOutline} style={{ width: 12, height: 12 }} />
              Live auction
            </span>
          )
        }

        // Scrim
        let scrim: React.ReactNode = null
        if (isWon) {
          scrim = (
            <div className="as-scrim">
              <span className="as-scrim-label as-scrim-label--won">
                <IonIcon icon={checkmarkOutline} style={{ width: 14, height: 14 }} />
                Sold out
              </span>
            </div>
          )
        } else if (isClosedOther) {
          scrim = (
            <div className="as-scrim">
              <span className="as-scrim-label as-scrim-label--closed">
                <IonIcon icon={lockClosedOutline} style={{ width: 14, height: 14 }} />
                Sold out
              </span>
            </div>
          )
        }

        // Heart (watchlist only, hidden on sold-out cards)
        const showHeart = !isWon && !isClosedOther && accountStatus !== 'guest'
        const heart_btn = showHeart ? (
          <button className="as-heart" onClick={onWatchListClick(item.itemid)}>
            <IonIcon
              icon={item.watchlist ? heart : heartOutline}
              style={{ width: 16, height: 16, color: item.watchlist ? '#d2222d' : '#888' }}
            />
          </button>
        ) : null

        // Price line
        let priceLine: React.ReactNode
        if (item.buyitnowonly) {
          priceLine = <span className="as-card-price as-card-price--soft"></span>
        }
        else if (isLive) {
          priceLine = <span className="as-card-price as-card-price--soft">Bid at the event</span>
        } else if (isWon || isClosedOther) {
          priceLine = <span className="as-card-price as-card-price--soft">Closed</span>
        } else {
          priceLine = <span className="as-card-price">${item.lastbid}</span>
        }

        const time = (isWon || isClosedOther) ? 'Ended' : itemStatus(item)

        // Buy now line
        const buyNowLine = (item.buyitnowavailable || item.buyitnowonly) && !isClosed && !isSoldOut
          ? <div className="as-card-buynow">
              Buy now · ${item.buyitnowprice}
              <span className="as-card-time">{time}</span>
            </div>
            // <div className="as-card-buynow">Buy now · ${item.buyitnowprice}</div>
          : null

        return (
          <div
            key={item.itemid}
            className={cardCls}
            onClick={() => onItemClick(item.itemid)}
          >
            <div className="as-thumb">
              {!item.imageurl && (
                <div className="as-no-img">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="30" height="30" style={{ opacity: 0.6 }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  <span>No image available</span>
                </div>
              )}
              {item.imageurl && (
                <LazyLoadImage
                  alt={item.name}
                  src={item.imageurl}
                  effect="blur"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
              )}
              {pill}
              {heart_btn}
              {scrim}
            </div>
            <div className="as-card-name" title={item.name}>{item.name}</div>
            <div className="as-card-meta">
              {priceLine}
              {!buyNowLine && <span className="as-card-time">{time}</span>}
            </div>
            {buyNowLine}
          </div>
        )
      })}
    </>
  )
}
